import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { storage } from './firebase';
import type { AdLevel } from '../types';

/**
 * Upload an ad image for a specific campaign and level.
 * Stored at: ads/{campaignId}/level{N}.{ext}
 * @returns Firebase Storage download URL
 */
export async function uploadAdImage(
  campaignId: string,
  level: AdLevel,
  file: File
): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg';
  const path = `ads/${campaignId}/level${level}.${ext}`;
  const storageRef = ref(storage, path);

  await uploadBytes(storageRef, file, {
    contentType: file.type,
    customMetadata: { campaignId, level: String(level) },
  });

  return getDownloadURL(storageRef);
}

/**
 * Delete all ad images for a campaign (all 3 level files).
 * Called when a campaign is deleted.
 */
export async function deleteCampaignImages(campaignId: string): Promise<void> {
  try {
    const campaignRef = ref(storage, `ads/${campaignId}`);
    const listResult = await listAll(campaignRef);

    await Promise.all(listResult.items.map(itemRef => deleteObject(itemRef)));
  } catch (error) {
    // Storage folder may not exist if images were never uploaded — not a fatal error
    console.warn(`Could not delete storage files for campaign ${campaignId}:`, error);
  }
}

/**
 * Upload an icon image for a stat panel.
 * Stored at: stat_panels/{panelId}/icon.{ext}
 * @returns Firebase Storage download URL
 */
export async function uploadStatPanelIcon(panelId: string, file: File): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? 'png';
  const path = `stat_panels/${panelId}/icon.${ext}`;
  const storageRef = ref(storage, path);

  await uploadBytes(storageRef, file, {
    contentType: file.type,
    customMetadata: { panelId },
  });

  return getDownloadURL(storageRef);
}

/**
 * Delete the icon file(s) for a stat panel.
 * Called when a panel is deleted.
 */
export async function deleteStatPanelIcon(panelId: string): Promise<void> {
  try {
    const folder = ref(storage, `stat_panels/${panelId}`);
    const listResult = await listAll(folder);
    await Promise.all(listResult.items.map(itemRef => deleteObject(itemRef)));
  } catch (error) {
    console.warn(`Could not delete icon for stat panel ${panelId}:`, error);
  }
}
